import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the grid
import "ag-grid-community/styles/ag-theme-quartz.css"; // Optional Theme applied to the grid
import { AgGridReact } from "ag-grid-react"; // AG Grid Component
import CardDropdown from "components/common/CardDropdown";
import IconButton from "components/common/IconButton";
import TableHeaderDropdown from "components/common/TableHeaderDropdown";
import { settings } from "config";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Card, Dropdown, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { MdOutlineModeEdit } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { CONSTANT_ROUTES } from "routes";
import {
  resetColumnState,
  restoreColumnState,
  saveColumnState,
} from "utils/tablesUtils";
import "./styles.css";
import { VouchersAPI, VouchersPostAPI } from "api";
import { useSettingContext } from "context/SettingContext";
import { formatDate } from "date-fns";
import PageTitle from "utils/PageTitle";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { customSettings } from "utils/customConfig";
import BreadCrumbTable from "components/breadcrumb/BreadCrumbTable";

export default function BatchPostingTable() {
  let isDarkBool = localStorage.getItem("isDark") === true;
  let isDarkTheme = localStorage.getItem("theme") === "dark";
  let auto = localStorage.getItem("theme") === "auto";
  const navigate = useNavigate();
  const columnApiRef = useRef();
  const gridRef = useRef();
  const [selectedCount, setSelectedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState("NotPosted");

  // Column Definitions: Defines the columns to be displayed.
  const [colDefs, setColDefs] = useState([
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      field: "checkbox",
      headerName: "",
      width: 50,
      // pinned: "left",
      suppressMenu: true,
      sortable: false,
      filter: false,
      resizable: false,
    },
    {
      field: "number",
      headerName: "Number",
      hide: false,
      flex: 2,
    },
    {
      field: "type",
      headerName: "Type",
      hide: false,
      flex: 2.5,
    },
    {
      field: "date",
      headerName: "Date",
      hide: false,
      flex: 1.5,
      cellRenderer: (p) => {
        const date = p?.data?.date
          ? new Date(p.data?.date).toLocaleDateString()
          : "";
        return <div>{date}</div>;
      },
    },
    {
      field: "postingDate",
      headerName: "Posting Date",
      hide: false,
      flex: 1.5,
      cellRenderer: (p) => {
        const date = p?.data?.postingDate
          ? new Date(p.data.postingDate).toLocaleDateString()
          : "";
        return <div>{date}</div>;
      },
    },
    {
      field: "postingStatus",
      headerName: "Posting Status",
      hide: false,
      flex: 1.5,
    },
    {
      field: "financeBooksArray",
      headerName: "Finance Books",
      hide: false,
      flex: 1.5,
      cellRenderer: (p) => {
        const fBook = p?.data?.financeBooksArray
          ? p?.data?.financeBooksArray.map((d) => d).join(", ")
          : "";
        return <div>{fBook}</div>;
      },
    },
    {
      headerName: "Action",
      field: "action",
      flex: 1.5,
      filter: false,
      headerClass: "center-header",
      cellStyle: {
        display: "flex",
        justifyContent: "center",
      },
      cellRenderer: (p) => (
        <CardDropdown>
          <div className="py-2">
            <Dropdown.Item
              // as={Link}
              onClick={() =>
                // navigate(`/libraries/categories/add-new-category/?id=${p.data.id}`)
                // () =>
                handleRowClick(p.data)
              }
              // href="/products/add-product"
              className="d-flex align-items-center gap-2 py-0"
            >
              <MdOutlineModeEdit />
              Edit
            </Dropdown.Item>
            {/* <Dropdown.Item
              // onClick={() => handleDelete()}
              className="d-flex align-items-center gap-2 py-0"
            >
              <MdDeleteOutline />
              Delete
            </Dropdown.Item> */}
          </div>
        </CardDropdown>
      ),
    },
  ]);

  const settingsData = useSettingContext();

  const costCenter = useMemo(
    () => settingsData?.find((d) => d.name === "DEFAULT_COST_CENTER"),
    [settingsData]
  );

  const costCenterId = Number(costCenter?.value); // Ensure it's a number

  const handleRowClick = (data) => {
    if (data) {
      navigate(`${CONSTANT_ROUTES.VOUCHER_ADD}`, { state: data });
    }
  };

  const defaultColDef = {
    sortable: true,
    filter: true,
    // floatingFilter: true,
    // flex: 1,
    // rowDrag: true,
  };

  //   ===============Custom Api Call start================

  const gridApiRef = useRef(null);

  const onGridReady = useCallback((e) => {
    gridApiRef.current = e.api; // Store API in ref
    restoreState();
    fetchData();
  }, []);

  const fetchData = useCallback(() => {
    if (!gridApiRef.current) return; // Ensure API is set

    // gridApiRef.current.showLoadingOverlay();

    if (!costCenterId) {
      // gridApiRef.current.setRowData([]); // Clear table
      // gridApiRef.current.showNoRowsOverlay(); // Show "No records found"
      return;
    }

    setLoading(true); // ✅ Show spinner
    gridApiRef.current.showLoadingOverlay(); // ✅ AG Grid overlay

    VouchersAPI.get({ params: { costCenterId } })
      .then((res) => {
        const allData = res?.data?.data || [];

        // Filter by posting status
        const filteredData =
          filters === "Posted"
            ? allData.filter((item) => item.postingStatus === "POSTED")
            : allData.filter(
                (item) =>
                  item.postingStatus === null ||
                  item.postingStatus === undefined ||
                  item.postingStatus === ""
              );

        gridApiRef.current.setRowData(filteredData);
      })
      .catch(() => {
        gridApiRef.current.setRowData([]); // Clear on failure
      })
      .finally(() => {
        setLoading(false);
        gridApiRef.current.hideOverlay();
      });
  }, [costCenterId, filters]);

  useEffect(() => {
    fetchData();
  }, [costCenterId, filters, fetchData]);

  const saveState = useCallback(() => {
    saveColumnState("vouchers-table", columnApiRef);
  }, []);

  const restoreState = useCallback(() => {
    restoreColumnState("vouchers-table", columnApiRef);
  }, []);

  const resetState = useCallback(() => {
    resetColumnState("vouchers-table", columnApiRef);
  }, []);

  const onFilterTextBoxChanged = useCallback(() => {
    columnApiRef.current.api.setGridOption(
      "quickFilterText",
      document.getElementById("filter-text-box").value
    );
  }, []);

  const handlePosting = async () => {
    const selectedRows = gridApiRef?.current?.getSelectedRows();
    const selectedRowIds = selectedRows.map((row) => row.id);

    console.log("Selected Rows IDS:", selectedRowIds);

    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to post the selected vouchers?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          await VouchersPostAPI.post(selectedRowIds);
          toast.success("Voucher Posted Successfully", {
            position: customSettings.toastPosition,
            autoClose: customSettings.toastClose,
          });
          fetchData(); // ✅ Refresh grid after posting
          setSelectedCount(0); // ✅ Reset selected count
          gridApiRef.current.deselectAll(); // ✅ Clear selection
        } catch (error) {
          console.log(error);
          toast.error("Something went wrong", {
            position: customSettings.toastPosition,
            autoClose: customSettings.toastClose,
          });
        }
        setLoading(false);
      }
    });
  };

  console.log(filters, "filters");

  const onSelectionChanged = useCallback(() => {
    const selectedRows = gridApiRef.current?.getSelectedRows() || [];
    setSelectedCount(selectedRows?.length);
    // console.log("Selected Rows:", selectedRows);
  }, []);

  return (
    <div>
      {/* <div className="py-2">
        <BreadCrumb />
      </div> */}
      <PageTitle name="Accounts | Vouchers | Posting" />

      <div
        className={`${
          isDarkBool || isDarkTheme || auto
            ? "ag-theme-quartz-dark"
            : "ag-theme-quartz"
        } rounded-0`} // applying the grid theme
        // style={{ height: "100vh" }} // the grid will fill the size of the parent container
      >
        <Card style={{ height: `calc(100vh - 4rem)`, overflow: "auto" }}>
          <Card.Header className="d-flex justify-content-between align-items-center">
            {/* <h5 className="fs-9 mb-0 text-nowrap py-2 py-xl-0">
              {" "}
              Accounts | Vouchers | Posting
            </h5> */}
            <BreadCrumbTable
              title="Accounts"
              subTitle="Vouchers"
              subTitle2="Posting"
            />
            <div className="d-flex align-items-center gap-2">
              {selectedCount > 0 && <div>{selectedCount} selected</div>}
              <Form.Select
                aria-label="Fiscal Year"
                className="fs-9"
                style={{ width: "180px" }}
                value={filters}
                onChange={(e) => {
                  setFilters(e.target.value);
                }}
              >
                <option value="NotPosted">Not Posted</option>
                <option value="Posted">Posted</option>
              </Form.Select>
              <Form className="position-relative">
                <FontAwesomeIcon
                  icon="search"
                  className="position-absolute text-400 search-box-icon"
                  style={{
                    left: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
                <Form.Control
                  type="search"
                  placeholder="Search..."
                  style={{
                    paddingLeft: "30px",
                  }}
                  aria-label="Search"
                  id="filter-text-box"
                  className="rounded-pill search-input"
                  onInput={onFilterTextBoxChanged}
                />
              </Form>
              <OverlayTrigger
                overlay={
                  <Tooltip
                    style={{ position: "fixed" }}
                    id="overlay-trigger-example"
                  >
                    Post
                  </Tooltip>
                }
              >
                <IconButton
                  variant="falcon-default"
                  size="sm"
                  icon="check"
                  transform="shrink-3"
                  onClick={handlePosting}
                  disabled={selectedCount === 0}
                >
                  <span className="d-none d-sm-inline-block ms-1">Post</span>
                </IconButton>
              </OverlayTrigger>
              <TableHeaderDropdown>
                <div className="py-2">
                  <Dropdown.Item
                    onClick={saveState}
                    className="d-flex align-items-center gap-2 py-2"
                  >
                    Save State
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={restoreState}
                    className="d-flex align-items-center gap-2 py-2"
                  >
                    Restore State
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={resetState}
                    className="d-flex align-items-center gap-2 py-2"
                  >
                    Reset State
                  </Dropdown.Item>
                </div>
              </TableHeaderDropdown>
            </div>
          </Card.Header>

          <Card.Body className="p-0">
            <AgGridReact
              // rowData={rowData}
              ref={columnApiRef}
              pagination
              paginationPageSize={settings.paginationSize}
              onGridReady={onGridReady}
              columnDefs={colDefs}
              headerHeight={35}
              rowHeight={35}
              defaultColDef={defaultColDef}
              onRowDoubleClicked={(params) => handleRowClick(params.data)}
              overlayNoRowsTemplate="Nothing found - Looks empty over here"
              rowSelection="multiple" // ✅ Allow multiple row selection
              suppressRowClickSelection={true} // ✅ Prevents row selection on cell click
              onSelectionChanged={onSelectionChanged}
            />
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}
